import React, { useEffect, useState } from 'react';
import BusinessAreasDropdown from '../../components/common/BusinessAreasDropdown';
import Dropdown from '../../components/common/dropdown';
import ScrollArea from '../../components/common/scrollArea';
import UpdatePasswordPopup from '../../components/common/UpdatePasswordPopup';
import { Divider } from '../../components/sidebar/components/Divider';
import { useOrganization } from '../../hooks/useOrganization';
import { useUsers } from '../../hooks/useUsers';
import businessAreasData from '../Onboarding/common/business-areas.json';
import industryOptsData from '../Onboarding/common/industryOpts.json';
import rolesData from '../Onboarding/common/roles.json';
import SimpleDropdown from '../../components/common/SimpleDropdown';

const scrollDropdownIntoView = (dropdownElement: HTMLElement) => {
  const scrollArea = dropdownElement.closest('.overflow-y-auto');
  if (!scrollArea) return;
  
  const rect = dropdownElement.getBoundingClientRect();
  const scrollRect = scrollArea.getBoundingClientRect();
  const dropdownHeight = 180; // Height of dropdown menu
  const bottomSpace = scrollRect.bottom - rect.bottom;
  const padding = 20;

  if (bottomSpace < dropdownHeight) {
    const scrollNeeded = dropdownHeight - bottomSpace + padding;
    scrollArea.scrollBy({
      top: scrollNeeded,
      behavior: 'smooth'
    });
  }
};

const ProfilePage = () => {
  const { user, updateUser } = useUsers();
  const { organization, updateOrganization, isLoading } = useOrganization();
  const [formData, setFormData] = useState({
    name: organization?.name || '',
    website: organization?.website || organization?.linkedin_company_profile?.company_website || '',
    industry: organization?.linkedin_company_profile?.industry || '',
    industry_other: '',
    size: organization?.linkedin_company_profile?.size || '',
    role: user?.role || '',
    role_other: '',
    businessAreas: organization?.business_areas || []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordPopupOpen, setIsPasswordPopupOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    website: '',
    industry: '',
    industry_other: '',
    size: '',
    role: '',
    role_other: '',
    businessAreas: [] as string[]
  });
  const [isToastVisible, setIsToastVisible] = useState(false);

  const inputStyles = `
    w-full h-[34px] 
    px-3 py-[9px] 
    border border-[#EEEFF1] 
    rounded-[10px] 
    text-[#222222] 
    text-sm 
    font-normal 
    leading-[16.94px] 
    tracking-[0.28px] 
    text-left 
    font-[var(--fontfamilyFont1)] 
    focus:outline-none 
    focus:ring-1 
    focus:ring-[#42E56F] 
    focus:border-transparent
    placeholder:text-[#DDDDDD]
    placeholder:font-light
    placeholder:text-sm
    [&>option]:px-3
    [&>option]:py-2
    [&>option]:bg-white
    [&>option]:text-[#222222]
    appearance-none
  `;

  const disabledInputStyles = `
    ${inputStyles}
    bg-[#FAFAFA]
    cursor-not-allowed
  `;

  const industryOptions = industryOptsData.industries.map(industry => ({
    value: industry,
    label: industry
  }));

  const companySizeOptions = [
    { value: '0-1', label: '0-1 employees' },
    { value: '2-10', label: '2-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1,000 employees' },
    { value: '1001-5000', label: '1,001-5,000 employees' },
    { value: '5001-10000', label: '5,001-10,000 employees' },
    { value: '10001+', label: '10,001+ employees' }
  ];

  const roleOptions = rolesData.roles.map(role => ({
    value: role,
    label: role
  }));

  const businessAreaOptions = businessAreasData.business_areas.map(area => ({
    value: area,
    label: area
  }));

  useEffect(() => {
    if (organization && user) {
      const roleValue = user.role || '';
      const roleExists = rolesData.roles.some(
        role => role.toLowerCase() === roleValue.toLowerCase()
      );

      const newFormData = {
        name: organization.name || '',
        website: organization.website || organization.linkedin_company_profile?.company_website || '',
        industry: organization.linkedin_company_profile?.industry || '',
        industry_other: '',
        size: organization.linkedin_company_profile?.size || '',
        role: roleExists ? roleValue : 'other',
        role_other: !roleExists ? roleValue : '',
        businessAreas: organization.business_areas || []
      };

      setFormData(newFormData);
      setOriginalFormData(newFormData);
      setHasChanges(false);
    }
  }, [organization, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
    checkForChanges(newFormData);
  };

  const handleBusinessAreasChange = (selectedValues: string[]) => {
    const newFormData = { ...formData, businessAreas: selectedValues };
    setFormData(newFormData);
    checkForChanges(newFormData);
  };

  const checkForChanges = (newData: typeof formData) => {
    const hasChanges = JSON.stringify(newData) !== JSON.stringify(originalFormData);
    console.log('Checking for changes:', hasChanges, newData, originalFormData);
    setHasChanges(hasChanges);
  };

  const handleSubmit = async () => {
    try {
      const userRole = formData.role === 'other' ? formData.role_other : formData.role;
      await updateUser({ role: userRole });
      await updateOrganization({
        linkedin_company_profile: {
          name: formData.name,
          company_website: formData.website,
          industry: formData.industry,
          size: formData.size,
        },
        business_areas: formData.businessAreas,
        current_plan: organization?.current_plan
      });
      setOriginalFormData(formData);
      setHasChanges(false);
      setIsToastVisible(true);
      setTimeout(() => setIsToastVisible(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center mx-auto h-screen">
      <div className="flex flex-col w-[762px] text-[Nunito Sans] px-[1px] h-full">
        {/* Header */}
        <div className="flex items-start mt-5 mb-2">
          <div className="text-start text-sm font-light text-[#75777C]">
            Workspace settings
          </div>
          <div className="text-start text-sm font-light text-[#222222] pl-2">
            / Profile
          </div>
        </div>

        {/* ScrollArea with full height */}
        <ScrollArea maxHeight="calc(100vh - 80px)" className="pb-[250px]">
          <div className="flex flex-col">
            {/* Page Title */}
            <div className="flex flex-col mb-7">
              <div className="flex flex-col mt-5 text-left gap-[2px]">
                <div className="text-xl font-semibold text-[#222222] ">Profile</div>
                <div className="text-sm font-light text-[#75777C] ">
                  Manage your personal details
                </div>
              </div>
            </div>

            {/* Info Notice */}
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 rounded-lg">
              <svg className="w-4 h-4 text-[#75777C]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
              </svg>
              <span className="text-sm text-[#5C5E63]">Changes to your profile will apply to your workspace</span>
            </div>

            <Divider className="w-full bg-[#EEEFF1] my-7" />

            {/* Work Email Section */}
            <div className="flex flex-col">
              <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col gap-6">
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-2 text-left">Work email</h3>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className={disabledInputStyles}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-6 text-left">
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-2 text-left">Password</h3>
                    <div className="relative">
                      <input
                        type="password"
                        value="••••••••"
                        disabled
                        className={disabledInputStyles}
                      />
                    </div>
                    <button
                      className="mt-2 text-sm text-[#5C5E63] hover:text-[#222222] transition-colors underline pl-3"
                      onClick={() => setIsPasswordPopupOpen(true)}
                    >
                      Change password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] my-7" />

            {/* Company Details Section */}
            <div className="flex flex-col mb-7 text-left">
              <h2 className="text-[16px] font-normal text-[#232529]">Company details</h2>
              <div className="text-sm font-light text-[#5C5E63] mb-7">Manage your company's information</div>
              <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col gap-6 pl-[1px]">
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-1 text-left">Name</h3>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={inputStyles}
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-1 text-left">Industry</h3>
                    <SimpleDropdown
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      options={industryOptions}
                      placeholder="Select industry"
                      allowOther={true}
                      otherValue={formData.industry_other}
                      onOtherChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          industry_other: value
                        }));
                      }}
                      className={inputStyles}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-6 pr-[1px]">
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-1 text-left">Size</h3>
                    <SimpleDropdown
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                      options={companySizeOptions}
                      placeholder="Select size"
                      className={inputStyles}
                    />
                  </div>
                  <div>
                  <h3 className="text-sm font-normal text-[#75777C] mb-1 text-left">Website</h3>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className={inputStyles}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] mb-7" />

            {/* Your Details Section */}
            <div className="flex flex-col mb-[10px] text-left">
              <h2 className="text-[16px] font-normal text-[#232529]">Your details</h2>
              <div className="text-sm font-light text-[#5C5E63] mb-7">Manage your personal details</div>
              <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col gap-6 pl-[1px]">
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-1 text-left">Role</h3>
                    <SimpleDropdown
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      options={roleOptions}
                      placeholder="Select role"
                      allowOther={true}
                      otherValue={formData.role_other}
                      onOtherChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          role_other: value
                        }));
                      }}
                      className={inputStyles}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-6 pr-[1px]">
                  <div>
                    <h3 className="text-sm font-normal text-[#75777C] mb-1 text-left">Business areas for use of Clodexa</h3>
                    <BusinessAreasDropdown
                      name="businessAreas"
                      value={formData.businessAreas}
                      onChange={handleBusinessAreasChange}
                      options={businessAreaOptions}
                      placeholder="Select business areas"
                      allowOther={true}
                      className={inputStyles}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Fixed save button container - Always visible */}
        <div className="fixed bottom-0 w-[762px] bg-white border-t border-[#EEEFF1] py-4 px-[1px] z-[2]">
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setFormData(originalFormData)}
              disabled={isLoading}
              className="px-2 py-1 rounded-lg h-[34px] transition-colors bg-[#EEEFF1] text-[#75777C] hover:bg-[#DDDDDD]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!hasChanges || isLoading}
              className={`px-2 py-1 rounded-lg h-[34px] transition-colors ${
                hasChanges 
                  ? 'bg-[#42E56F] text-[#222222] hover:bg-[#3bd063]' 
                  : 'bg-[#EEEFF1] text-[#75777C] cursor-not-allowed'
              }`}
            >
              {isLoading ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <UpdatePasswordPopup
        isOpen={isPasswordPopupOpen}
        onClose={() => setIsPasswordPopupOpen(false)}
      />

      {isToastVisible && (
        <div className="fixed top-4 right-4 bg-green-100 text-green-700 p-4 rounded-lg shadow-lg">
          Profile updated successfully!
        </div>
      )}
    </div>
  );
};

export default ProfilePage; 